import TitleIcon from '@mui/icons-material/Title';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import ThumbsUpDownIcon from '@mui/icons-material/ThumbsUpDown';
import ShortTextIcon from '@mui/icons-material/ShortText';
import NotesIcon from '@mui/icons-material/Notes';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LinkIcon from '@mui/icons-material/Link';
import TagIcon from '@mui/icons-material/Tag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GestureIcon from '@mui/icons-material/Gesture';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArticleIcon from '@mui/icons-material/Article';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import type { SvgIconComponent } from '@mui/icons-material';

export const QUESTION_TYPE_KEYS = {
    FORM_TITLE: 'form-title',
    SECTION_HEADER: 'section-header',
    YES_NO: 'yes-no',
    SHORT_TEXT: 'short-text',
    LONG_TEXT: 'long-text',
    MULTIPLE_CHOICE: 'multiple-choice',
    LINK: 'link',
    NUMBER: 'number',
    DATE: 'date',
    FILE_UPLOAD: 'file-upload',
    PDF_ATTACHMENT: 'pdf-attachment',
    SIGNATURE: 'signature',
    CONSENT_CHECKBOX: 'consent-checkbox',
    FREE_TEXT: 'free-text',
    EMAIL: 'email',
    PHONE: 'phone',
    RATING: 'rating'
} as const;

export type QuestionTypeKey = (typeof QUESTION_TYPE_KEYS)[keyof typeof QUESTION_TYPE_KEYS];

// Numeric identifiers for each question type (for mobile app compatibility)
export const QUESTION_TYPE_IDS: Record<QuestionTypeKey, number> = {
    [QUESTION_TYPE_KEYS.FORM_TITLE]: 1,
    [QUESTION_TYPE_KEYS.SECTION_HEADER]: 2,
    [QUESTION_TYPE_KEYS.YES_NO]: 3,
    [QUESTION_TYPE_KEYS.SHORT_TEXT]: 4,
    [QUESTION_TYPE_KEYS.LONG_TEXT]: 5,
    [QUESTION_TYPE_KEYS.MULTIPLE_CHOICE]: 6,
    [QUESTION_TYPE_KEYS.LINK]: 7,
    [QUESTION_TYPE_KEYS.NUMBER]: 8,
    [QUESTION_TYPE_KEYS.DATE]: 9,
    [QUESTION_TYPE_KEYS.FILE_UPLOAD]: 10,
    [QUESTION_TYPE_KEYS.PDF_ATTACHMENT]: 11,
    [QUESTION_TYPE_KEYS.SIGNATURE]: 12,
    [QUESTION_TYPE_KEYS.CONSENT_CHECKBOX]: 13,
    [QUESTION_TYPE_KEYS.FREE_TEXT]: 15,
    [QUESTION_TYPE_KEYS.EMAIL]: 16,
    [QUESTION_TYPE_KEYS.PHONE]: 17,
    [QUESTION_TYPE_KEYS.RATING]: 18
};

export interface QuestionTypeMeta {
    label: string;
    description: string;
    icon: SvgIconComponent;
    category: string;
}

export const QUESTION_TYPES: Record<QuestionTypeKey, QuestionTypeMeta> = {
    [QUESTION_TYPE_KEYS.FORM_TITLE]: {
        label: 'כותרת טופס',
        description: 'כותרת ראשית לטופס',
        icon: TitleIcon,
        category: 'headers'
    },
    [QUESTION_TYPE_KEYS.SECTION_HEADER]: {
        label: 'כותרת מקטע',
        description: 'כותרת להפרדת מקטעים',
        icon: SubtitlesIcon,
        category: 'headers'
    },
    [QUESTION_TYPE_KEYS.YES_NO]: {
        label: 'כן / לא',
        description: 'שאלה סגורה עם פירוט',
        icon: ThumbsUpDownIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.SHORT_TEXT]: {
        label: 'טקסט קצר',
        description: 'שורת טקסט בודדת',
        icon: ShortTextIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.LONG_TEXT]: {
        label: 'טקסט ארוך',
        description: 'תיבת טקסט מרובת שורות',
        icon: NotesIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.MULTIPLE_CHOICE]: {
        label: 'בחירה מרובה',
        description: 'רדיו או צ\'קבוקס',
        icon: CheckBoxIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.LINK]: {
        label: 'לינק / קישור',
        description: 'קישור חיצוני',
        icon: LinkIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.NUMBER]: {
        label: 'מספר',
        description: 'שדה מספרי',
        icon: TagIcon,
        category: 'numeric'
    },
    [QUESTION_TYPE_KEYS.DATE]: {
        label: 'תאריך',
        description: 'בחירת תאריך',
        icon: CalendarTodayIcon,
        category: 'numeric'
    },
    [QUESTION_TYPE_KEYS.FILE_UPLOAD]: {
        label: 'העלאת קובץ',
        description: 'העלאת מסמך או תמונה',
        icon: UploadFileIcon,
        category: 'files'
    },
    [QUESTION_TYPE_KEYS.PDF_ATTACHMENT]: {
        label: 'PDF מוצמד',
        description: 'קובץ PDF לצפייה',
        icon: PictureAsPdfIcon,
        category: 'files'
    },
    [QUESTION_TYPE_KEYS.SIGNATURE]: {
        label: 'חתימה דיגיטלית',
        description: 'פאד חתימה',
        icon: GestureIcon,
        category: 'consent'
    },
    [QUESTION_TYPE_KEYS.CONSENT_CHECKBOX]: {
        label: 'אישור הסכמה',
        description: 'סימון הסכמה לתנאים',
        icon: VerifiedUserIcon,
        category: 'consent'
    },
    [QUESTION_TYPE_KEYS.FREE_TEXT]: {
        label: 'טקסט חופשי',
        description: 'בלוק טקסט להסבר',
        icon: ArticleIcon,
        category: 'other'
    },
    [QUESTION_TYPE_KEYS.EMAIL]: {
        label: 'אימייל',
        description: 'שדה כתובת אימייל',
        icon: EmailIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.PHONE]: {
        label: 'טלפון',
        description: 'שדה מספר טלפון',
        icon: PhoneIcon,
        category: 'questions'
    },
    [QUESTION_TYPE_KEYS.RATING]: {
        label: 'דירוג',
        description: 'דירוג כוכבים',
        icon: StarIcon,
        category: 'questions'
    }
};

export interface QuestionCategory {
    label: string;
    types: QuestionTypeKey[];
}

export const QUESTION_CATEGORIES: Record<string, QuestionCategory> = {
    headers: {
        label: 'כותרות ומבנה',
        types: [QUESTION_TYPE_KEYS.FORM_TITLE, QUESTION_TYPE_KEYS.SECTION_HEADER]
    },
    questions: {
        label: 'שאלות',
        types: [
            QUESTION_TYPE_KEYS.YES_NO,
            QUESTION_TYPE_KEYS.SHORT_TEXT,
            QUESTION_TYPE_KEYS.LONG_TEXT,
            QUESTION_TYPE_KEYS.MULTIPLE_CHOICE,
            QUESTION_TYPE_KEYS.LINK,
            QUESTION_TYPE_KEYS.EMAIL,
            QUESTION_TYPE_KEYS.PHONE,
            QUESTION_TYPE_KEYS.RATING
        ]
    },
    numeric: {
        label: 'מספרים ותאריכים',
        types: [QUESTION_TYPE_KEYS.NUMBER, QUESTION_TYPE_KEYS.DATE]
    },
    files: {
        label: 'קבצים',
        types: [QUESTION_TYPE_KEYS.FILE_UPLOAD, QUESTION_TYPE_KEYS.PDF_ATTACHMENT]
    },
    consent: {
        label: 'הסכמות וחתימה',
        types: [
            QUESTION_TYPE_KEYS.SIGNATURE,
            QUESTION_TYPE_KEYS.CONSENT_CHECKBOX
        ]
    },
    other: {
        label: 'אחר',
        types: [QUESTION_TYPE_KEYS.FREE_TEXT]
    }
};

// UI form-builder question shape (local to the builder, not a server DTO).
export interface BuilderQuestion {
    id: string;
    type?: QuestionTypeKey | string;
    questionTypeId?: number;
    required?: boolean;
    [key: string]: unknown;
}

export const duplicateQuestion = <T extends { id?: string }>(question: T): T & { id: string } => {
    return {
        ...question,
        id: `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
};

export const getDefaultQuestionData = (type: QuestionTypeKey | string): BuilderQuestion => {
    const baseData: BuilderQuestion = {
        id: `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type,
        questionTypeId: QUESTION_TYPE_IDS[type as QuestionTypeKey] || 0,
        required: false
    };

    switch (type) {
        case QUESTION_TYPE_KEYS.FORM_TITLE:
            return { ...baseData, title: 'כותרת הטופס' };
        case QUESTION_TYPE_KEYS.SECTION_HEADER:
            return { ...baseData, title: 'כותרת מקטע' };
        case QUESTION_TYPE_KEYS.YES_NO:
            return { ...baseData, question: 'שאלת כן/לא', showDetailsOnYes: true, detailsPlaceholder: 'פירוט (אופציונלי)', showDetailsOnNo: false, detailsPlaceholderNo: '' };
        case QUESTION_TYPE_KEYS.SHORT_TEXT:
            return { ...baseData, question: 'שאלת טקסט קצר', placeholder: '' };
        case QUESTION_TYPE_KEYS.LONG_TEXT:
            return { ...baseData, question: 'שאלת טקסט ארוך', placeholder: '' };
        case QUESTION_TYPE_KEYS.MULTIPLE_CHOICE:
            return {
                ...baseData,
                question: 'שאלת בחירה מרובה',
                options: ['אפשרות 1', 'אפשרות 2'],
                allowMultiple: false
            };
        case QUESTION_TYPE_KEYS.LINK:
            return { ...baseData, question: 'קישור למידע נוסף', linkUrl: '', linkDescription: '' };
        case QUESTION_TYPE_KEYS.NUMBER:
            return { ...baseData, question: 'שדה מספרי' };
        case QUESTION_TYPE_KEYS.DATE:
            return { ...baseData, question: 'בחירת תאריך' };
        case QUESTION_TYPE_KEYS.FILE_UPLOAD:
            return { ...baseData, question: 'העלאת קובץ', allowedTypes: ['image', 'video', 'pdf', 'document'], fileUrl: null };
        case QUESTION_TYPE_KEYS.PDF_ATTACHMENT:
            return { ...baseData, question: 'קובץ PDF מוצמד', pdfUrl: null, pdfName: null };
        case QUESTION_TYPE_KEYS.SIGNATURE:
            return { ...baseData, question: 'חתימה דיגיטלית', signatureType: 'canvas' };
        case QUESTION_TYPE_KEYS.CONSENT_CHECKBOX:
            return { ...baseData, question: 'אני מאשר/ת ומסכים/ה לתנאים', consentType: 'general' };
        case QUESTION_TYPE_KEYS.FREE_TEXT:
            return { ...baseData, content: 'הזן טקסט חופשי כאן...' };
        case QUESTION_TYPE_KEYS.EMAIL:
            return { ...baseData, question: 'רשום את האימייל שלך', placeholder: 'example@email.com' };
        case QUESTION_TYPE_KEYS.PHONE:
            return { ...baseData, question: 'הוסף את הפלאפון שלך', placeholder: '050-0000000' };
        case QUESTION_TYPE_KEYS.RATING:
            return { ...baseData, question: 'בחר את הדירוג', maxRating: 5 };
        default:
            return { ...baseData, question: 'שאלה חדשה' };
    }
};
